import { useEffect } from 'react';

const upsertMeta = (attribute, key, content) => {
    if (!content) return;
    let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
};

const ensureTrailingSlash = (path) => path === '/blog' || path === '/blog/'
    ? '/blog/'
    : `${String(path || '').replace(/\/+$/, '')}/`;

const absoluteAssetUrl = (asset) => {
    if (asset.startsWith('http')) return asset;
    if (asset.startsWith('/blog/')) return `${window.location.origin}${asset}`;
    return `${window.location.origin}${process.env.PUBLIC_URL || ''}${asset}`;
};

const BlogSeo = ({
    title,
    ogTitle,
    description,
    canonicalPath,
    image,
    imageAlt,
    preloadImage,
    type = 'article',
    noIndex = false,
    publishedTime,
    modifiedTime,
}) => {
    useEffect(() => {
        const previousTitle = document.title;
        document.title = title || 'CliQloan Blog';

        upsertMeta('name', 'description', description);
        upsertMeta('property', 'og:title', ogTitle || title);
        upsertMeta('property', 'og:description', description);
        upsertMeta('property', 'og:type', type);
        upsertMeta('property', 'og:site_name', 'CliQloan');
        upsertMeta('property', 'og:locale', 'en_US');
        upsertMeta('name', 'theme-color', '#0f172a');
        upsertMeta('name', 'author', 'Raman Yakkala');
        upsertMeta('name', 'publisher', 'AmitaSoft, LLC');
        upsertMeta('name', 'twitter:card', 'summary_large_image');
        upsertMeta('name', 'twitter:title', ogTitle || title);
        upsertMeta('name', 'twitter:description', description);
        upsertMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow');
        if (type === 'article') {
            upsertMeta('property', 'article:published_time', publishedTime);
            upsertMeta('property', 'article:modified_time', modifiedTime);
        }

        if (canonicalPath) {
            const canonicalUrl = `${window.location.origin}${ensureTrailingSlash(canonicalPath)}`;
            upsertMeta('property', 'og:url', canonicalUrl);

            let link = document.head.querySelector('link[rel="canonical"]');
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', 'canonical');
                document.head.appendChild(link);
            }
            link.setAttribute('href', canonicalUrl);
        }

        if (image) {
            const imageUrl = absoluteAssetUrl(image);
            upsertMeta('property', 'og:image', imageUrl);
            upsertMeta('property', 'og:image:alt', imageAlt || title);
            upsertMeta('name', 'twitter:image', imageUrl);
        }

        const existingPreload = document.head.querySelector('link[data-blog-hero-preload="true"]');
        if (preloadImage) {
            const preloadUrl = absoluteAssetUrl(preloadImage);
            const preloadLink = existingPreload || document.createElement('link');
            preloadLink.setAttribute('rel', 'preload');
            preloadLink.setAttribute('as', 'image');
            preloadLink.setAttribute('type', preloadUrl.endsWith('.webp') ? 'image/webp' : 'image/avif');
            preloadLink.setAttribute('href', preloadUrl);
            preloadLink.setAttribute('fetchpriority', 'high');
            preloadLink.setAttribute('data-blog-hero-preload', 'true');
            if (!existingPreload) document.head.appendChild(preloadLink);
        } else if (existingPreload) {
            existingPreload.remove();
        }

        return () => {
            document.title = previousTitle;
        };
    }, [title, ogTitle, description, canonicalPath, image, imageAlt, preloadImage, type, noIndex, publishedTime, modifiedTime]);

    return null;
};

export default BlogSeo;
